import {flatten, get, groupBy, isEmpty, isNil} from 'lodash'
import {getPartTemplateIdFromPriceList} from 'helpers/partTemplates'
import {sortByKey} from 'helpers/sorting'

export const nullPriceList = {
  id: -1,
  name: null,
  order: 0,
  visible: false,
  createdDate: null,
  modifiedDate: null,
  guid: null,
}

export const mapPriceListsToOptions = (priceLists = []) => {
  return priceLists.map(priceList => {
    return {
      id: priceList.id,
      key: priceList.id,
      value: priceList.id,
      label: priceList.name,
    }
  })
}

const createArray = (length) => {
  const ret = []
  for (let i = 0; i < length; i++) {
    ret.push(i)
  }
  return ret
}

const createField = (key, label, group, divider) => ({
  key,
  label,
  group,
  divider,
})

export const FIELDS = [
  createField("partCode", "Part Code"),
  createField("desc", "Description"),
  createField("packType", "Pack Type"),
  createField("packQty", "Pack Quantity"),
  createField("partType", "Part Type"),
  createField("template", "Template"),
  ...createArray(30).map(i => createField(`category${i}`, `Category ${i + 1}`, 'Categorisation')),
  createField('standardCost', 'Standard Cost'),
  createField(`price${0}`, `Price`),
  createField('standardCost', 'Cost'),
  ...flatten(createArray(10).map(i => [
    createField(`minPricePacks${i}`, `Min Packs ${i + 1}`, 'Price Breaks', true),
    createField(`maxPricePacks${i}`, `Max Packs ${i + 1}`, 'Price Breaks'),
    createField(`price${i}`, `Price ${i + 1}`, 'Price Breaks'),
    createField(`discount${i}`, `Discount ${i + 1}`, 'Price Breaks'),
    createField(`cost${i}`, `Cost ${i + 1}`, 'Price Breaks'),
    createField(`markup${i}`, `Markup ${i + 1}`, 'Price Breaks'),
  ])),
  createField('supplier', 'Supplier'),
  createField('supplierCode', 'Supplier Code'),
  ...flatten(createArray(10).map(i => [
    createField(`minCostPacks${i}`, `Min Cost Packs ${i + 1}`, 'Supplier Cost Breaks', i > 0),
    createField(`maxCostPacks${i}`, `Max Cost Packs ${i + 1}`, 'Supplier Cost Breaks'),
    createField(`supplierCost${i}`, `Supplier Cost ${i + 1}`, 'Supplier Cost Breaks'),
    createField(`supplierDiscount${i}`, `Supplier Discount ${i + 1}`, 'Supplier Cost Breaks'),
  ])),
  createField('sizeX', 'Length', 'Sizing'),
  createField('sizeY', 'Width', 'Sizing'),
  createField('sizeZ', 'Height', 'Sizing'),
  createField('basePrice', 'Base Price', 'Advanced Pricing'),
  createField('baseCost', 'Base Cost', 'Advanced Pricing'),
  createField('minPrice', 'Min Price', 'Advanced Pricing'),
  createField('minCost', 'Min Cost', 'Advanced Pricing'),
  createField('msrp', 'RRP', 'Advanced Pricing'),
  createField('cumulative', 'Cumulative Price Breaks', 'Advanced Pricing'),
  createField('sizeFactored', 'Size Factored'),
  createField("supplierBaseCost", "Supplier Base Cost", 'Advanced Pricing'),
  createField("supplierMinCost", "Supplier Minimum Cost", 'Advanced Pricing'),
  createField('priceCurrency', 'Pricing Currency', 'Additional Part Information'),
  createField('supplierCurrency', 'Supplier Currency', 'Additional Part Information'),
  createField('taxCode', 'Tax Code', 'Additional Part Information'),
  createField('manCode', 'Manufacturer Code', 'Additional Part Information'),
  createField('status', 'Status', 'Additional Part Information'),
  createField('notes', 'Notes', 'Additional Part Information'),
  createField('nominalCode', 'Nominal Code', 'Additional Part Information'),
  createField('purchaseNominalCode', 'Purchase Nominal Code', 'Additional Part Information'),
]

export const getAllFields = () => {
  const fields = [
    ...FIELDS,
  ]

  return fields.map((field, index) => ({...field, index}))
}

export const getGroupedFields = (fields) => {
  const ret = groupFields(fields)
  ret.sort((a, b) => sortByKey(a, b, 'index'))
  return ret
}

const groupFields = (fields) => {
  const groupedFields = fields.filter(field => !!field.group)
  const ungroupedFields = fields.filter(field => !field.group)

  const groups = groupBy(groupedFields, 'group')

  const groupFields = Object.keys(groups).map(group => ({
    label: group,
    fields: groups[group],
    index: groups[group][0].index,
  }))

  return [
    ...groupFields,
    ...ungroupedFields,
  ]
}

export const getLatestAddedPriceList = (name, priceLists = []) => {
  return priceLists
    .filter(priceList => priceList.name === name)
    .reduce((a, b) => a.createdDate > b.createdDate ? a : b)
}

export const getPriceListForUpdating = (priceList, partTemplates = []) => {
  return {
    ...priceList,
    partTemplateId: getPartTemplateIdFromPriceList(priceList, partTemplates),
    taxCodeId: get(priceList, 'rootCategory.taxCodeId', null),
    markup: get(priceList, 'rootCategory.markup', null),
  }
}

export const checkPartsAndCategoriesCount = (priceListCategoriesCount, priceListPartsCount) => {
  return isNil(priceListCategoriesCount)
    || isNil(priceListPartsCount)
    || priceListCategoriesCount > 1
    || priceListPartsCount > 0
}

export const isBreakSelected = (costOrPriceBreak, quantity, partPack, selectedPackId) => {
  return partPack.id === selectedPackId &&
    quantity >= costOrPriceBreak.minPacks &&
    (costOrPriceBreak.maxPacks === 0 || quantity <= costOrPriceBreak.maxPacks)
}

export const isCostBreakSelected = (costBreak, quantity, partPack, selectedPackId) => {
  return isBreakSelected(costBreak, quantity, partPack, selectedPackId)
}

export const isPriceBreakSelected = (priceBreak, quantity, partPack, selectedPackId, sizeFactor, allPriceBreaks) => {
  if (!isPartPackSizeFactored(partPack) || sizeFactor === 1) {
    return isBreakSelected(priceBreak, quantity, partPack, selectedPackId)
  }
  const sizeBasedQuantity = getPriceBreakSizeBasedQuantity(quantity, sizeFactor)
  const roundedSizeBasedQuantity = getRoundedPriceBreakSizeBasedQuantity(allPriceBreaks, sizeBasedQuantity)

  return isBreakSelected(priceBreak, roundedSizeBasedQuantity, partPack, selectedPackId)
}

export const getPartDimensions = (part) => {
  const dimensions = [
    get(part, 'sizeX', 0),
    get(part, 'sizeY', 0),
    get(part, 'sizeZ', 0),
  ]
  return getNonZeroDimensions(dimensions)
}

export const getNonZeroDimensions = (dimensions) => {
  return dimensions.filter(d => d > 0)
}

export const getPriceBreakSizeFactor = (partDimensions, selectedDimensions) => {
  if (isEmpty(selectedDimensions)) {
    return 1
  }
  return selectedDimensions.reduce((a, b) => a * b) / partDimensions.reduce((a, b) => a * b, 1)
}

export const getPriceBreakSizeBasedQuantity = (quantity, sizeFactor) => {
  return quantity * sizeFactor
}

export const getRoundedPriceBreakSizeBasedQuantity = (priceBreaks, sizeBasedQuantity) => {
  const priceBreakValues = getAllPriceBreakValues(priceBreaks)
  const filteredPriceBreaks = priceBreakValues
    .filter(pb => pb <= sizeBasedQuantity)
    .sort((a, b) => b - a)
  return !isEmpty(filteredPriceBreaks) ? filteredPriceBreaks[0] : 1
}

export const isPartPackSizeFactored = (partPack) => {
  return get(partPack, 'sizeFactored', false)
}

export const getAllPriceBreakValues = (priceBreaks) => {
  const minPriceBreaks = priceBreaks.map(pb => pb.minPacks)
  const maxPriceBreaks = priceBreaks.map(pb => pb.maxPacks)
  return [...minPriceBreaks, ...maxPriceBreaks]
}

export const deleteEntityTypes = {
  CATEGORY: 'category',
  GROUP: 'group',
  PART: 'part',
}

export const getPartsDeleteEntityType = (partIds = []) => {
  return partIds.length > 1 ? deleteEntityTypes.GROUP : deleteEntityTypes.PART
}

export const getPartUsedInPricingCalculatorMessages = (entityType) => {
  return entityType === deleteEntityTypes.PART
    ? [
      'This part is used in Pricing Calculators in the Price List and cannot be deleted.',
      'To delete this part, remove it from all associated Pricing Calculators first.',
    ]
    : [
      `One or more parts within this ${entityType} are used in Pricing Calculators in the Price List and cannot be deleted.`,
      `To delete this ${entityType}, remove all affected parts from their associated Pricing Calculators first.`,
    ]
}
